import React from 'react';
import { connect } from 'react-redux';

import { baseMapStateToProps } from '../../componentBase';
import { ClippingPlatformState } from '../../../store/clippingPlatformState';
import {
  createAfterEffectsParticipant,
  getAfterEffectsParticipants,
  deleteAfterEffectsParticipant,
  editAfterEffectsParticipant,
} from '../../../api/clipGenV2/afterEffectsParticipants.actions';
import { callApi } from '../../../helpers/callApi/callApi';
import * as NotificationHelper from '../../../helpers/notificationHelper';
import { createModalOpenAction, createModalCloseAction } from '../../../actions/modalActions';
import { SimpleModalId } from '../../../constants/modalId';
import { generateSequentialNumberOptions } from '../../../helpers/optionsHelper';
import { getAfterEffectsChapters } from '../../../api/clipGenV2/afterEffectsChapters.actions';

import { ParticipantsTable } from './participantsTable';
import {
  CreateParticipantFormDataProps,
  EditParticipantFormDataProps,
  ParticipantManagementProps,
  ParticipantsRequestStates,
} from './participant.types';
import { CreateParticipantFormContainer } from './forms/createParticipantFormContainer';
import { EditParticipantFormContainer } from './forms/editParticipantFormContainer';

export class ParticipantManagement extends React.Component<ParticipantManagementProps, ParticipantsRequestStates> {
  state: ParticipantsRequestStates = {
    chapters: [],
    participants: [],
    getChaptersLoading: false,
    getChaptersError: false,
    getParticipantsLoading: false,
    getParticipantsError: false,
    createParticipantLoading: false,
    deleteParticipantLoading: false,
    participantToUpdate: null,
    editParticipantLoading: false,
  };

  componentDidMount() {
    this.getParticipants();
    this.getChapters();
  }

  getChapters = () => {
    const { getAEChapters } = this.props;

    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, getChaptersLoading: true }));

        return getAEChapters();
      },
      onSuccess: ({ payload }) => {
        this.setState(prev => ({ ...prev, chapters: payload, getChaptersLoading: false, getChaptersError: false }));
      },
      onFailure: () =>
        this.setState(prev => ({ ...prev, chapters: [], getChaptersLoading: false, getChaptersError: true })),
    });
  };

  getParticipants = () => {
    const { getAEParticipants } = this.props;

    callApi({
      action: () => {
        this.setState({ getParticipantsLoading: true });

        return getAEParticipants();
      },
      onSuccess: ({ payload }) => {
        this.setState({ participants: payload, getParticipantsLoading: false, getParticipantsError: false });
      },
      onFailure: () => this.setState({ participants: [], getParticipantsLoading: false, getParticipantsError: true }),
    });
  };

  createParticipant = (participant: CreateParticipantFormDataProps) => {
    const { createAEParticipant, closeCreateParticipantModal } = this.props;

    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, createParticipantLoading: true }));

        return createAEParticipant(participant);
      },
      onSuccess: () => {
        this.setState(prev => ({ ...prev, createParticipantLoading: false }));
        closeCreateParticipantModal();
        NotificationHelper.success('Successfully created participant!');
        this.getParticipants();
      },
      onFailure: () => {
        this.setState(prev => ({ ...prev, createParticipantLoading: false }));
        NotificationHelper.error('Failed to create participant!');
      },
    });
  };

  deleteParticipant = (participantId: string) => {
    const { deleteAEParticipant } = this.props;

    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, deleteParticipantLoading: true }));

        return deleteAEParticipant(participantId);
      },
      onSuccess: () => {
        this.setState(prev => ({ ...prev, deleteParticipantLoading: false }));
        NotificationHelper.success('Successfully deleted participant!');
        this.getParticipants();
      },
      onFailure: () => {
        this.setState(prev => ({ ...prev, deleteParticipantLoading: false }));
        NotificationHelper.error('Failed to delete participant!');
      },
    });
  };

  editParticipant = (participantNewValues: EditParticipantFormDataProps) => {
    const { participantToUpdate } = this.state;
    const { editAEParticipant, closeEditParticipantModal } = this.props;
    const participantId = participantToUpdate.id;
    const body = participantNewValues;
    delete body.id;

    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, editParticipantLoading: true }));

        return editAEParticipant(participantId, body);
      },
      onSuccess: () => {
        this.setState(prev => ({ ...prev, editParticipantLoading: false }));
        closeEditParticipantModal();
        NotificationHelper.success('Successfully updated participant!');
        this.getParticipants();
      },
      onFailure: () => {
        this.setState(prev => ({ ...prev, editParticipantLoading: false }));
        NotificationHelper.error('Failed to update participant!');
      },
    });
  };

  handleOpenEditParticipantModal = (participantToUpdate: EditParticipantFormDataProps) => {
    const { openEditParticipantModal } = this.props;
    this.setState(prev => ({ ...prev, participantToUpdate }));
    openEditParticipantModal();
  };

  render = () => {
    const {
      chapters,
      participants,
      getParticipantsLoading,
      getParticipantsError,
      createParticipantLoading,
      deleteParticipantLoading,
      participantToUpdate,
      editParticipantLoading,
    } = this.state;
    const highestOrderIndex = chapters.length + participants.length + 1;
    const orderIndexOptions = generateSequentialNumberOptions(highestOrderIndex);

    if (getParticipantsError) {
      return <div>Something went wrong</div>;
    }

    if (!getParticipantsLoading) {
      const isLoading =
        getParticipantsLoading || createParticipantLoading || deleteParticipantLoading || editParticipantLoading;

      return (
        <>
          <CreateParticipantFormContainer
            isLoading={createParticipantLoading}
            onSubmit={this.createParticipant}
            orderIndexOptions={orderIndexOptions}
            initialValues={{ orderIndex: highestOrderIndex }}
          />
          {participantToUpdate && (
            <EditParticipantFormContainer
              isLoading={editParticipantLoading}
              onSubmit={this.editParticipant}
              orderIndexOptions={orderIndexOptions}
              initialValues={participantToUpdate}
            />
          )}
          <ParticipantsTable
            participants={participants}
            isLoading={isLoading}
            onClickDelete={this.deleteParticipant}
            onClickEdit={this.handleOpenEditParticipantModal}
          />
        </>
      );
    }

    return null;
  };
}

const mapDispatchToProps = (dispatch: Function) => ({
  getAEChapters: () => dispatch(getAfterEffectsChapters()),
  getAEParticipants: () => dispatch(getAfterEffectsParticipants()),
  createAEParticipant: (participant: CreateParticipantFormDataProps) =>
    dispatch(createAfterEffectsParticipant(participant)),
  deleteAEParticipant: (participantId: string) => dispatch(deleteAfterEffectsParticipant(participantId)),
  editAEParticipant: (participantId: string, participant: EditParticipantFormDataProps) =>
    dispatch(editAfterEffectsParticipant(participantId, participant)),
  closeCreateParticipantModal: () => dispatch(createModalCloseAction(SimpleModalId.CreateAfterEffectsParticipantModal)),
  openEditParticipantModal: () => dispatch(createModalOpenAction(SimpleModalId.EditAfterEffectsParticipantModal)),
  closeEditParticipantModal: () => dispatch(createModalCloseAction(SimpleModalId.EditAfterEffectsParticipantModal)),
});

const mapStateToProps = (state: ClippingPlatformState) => baseMapStateToProps(state);

export const ParticipantManagementContainer = connect(mapStateToProps, mapDispatchToProps)(ParticipantManagement);
